/**
 * Dados e vocabulário compartilhados pelas abas de rastreamento.
 *
 * As três abas (controle, planejamento, mapa) leem a MESMA resposta de
 * `/rastreamento`, pela mesma chave de cache: trocar de aba não refaz a busca,
 * e os filtros da seção valem para todas.
 */

import { useQuery } from '@tanstack/react-query';
import { useOutletContext } from 'react-router-dom';
import { api } from '../../lib/api.ts';
import { formatarCep } from '../../lib/format.ts';

export type Caso =
  | 'ok'
  | 'falta_instalar'
  | 'instalado_sem_produto'
  | 'recolher'
  | 'migracao'
  | 'aguardando';

export type Faixa = 'ate7' | 'ate15' | 'ate30' | 'mais30';

export interface Associado {
  nome: string | null;
  cpf: string | null;
  celular: string | null;
  telefone: string | null;
  email: string | null;
  logradouro: string | null;
  numero: string | null;
  complemento: string | null;
  bairro: string | null;
  cidade: string | null;
  estado: string | null;
  cep: string | null;
}

export interface Item {
  placa: string | null;
  caso: Caso;
  veiculo: string | null;
  tipo: string | null;
  situacao: string | null;
  dataCadastro: string | null;
  diasSemInstalar: number | null;
  faixa: Faixa | null;
  classificacao: string | null;
  produtos: string | null;
  valorProdutos: number | null;
  temMigracao: boolean;
  ano: string | null;
  chassi: string | null;
  renavam: string | null;
  valorFipe: number | null;
  associado: Associado;
  /** Sede do município do associado, [lat, lon]. Nulo se a cidade não foi reconhecida. */
  geo: [number, number] | null;
  cooperativa: string | null;
  consultor: string | null;
  /** Último comunicado de suspensão enviado para a placa. */
  comunicado: {
    status: 'pendente' | 'enviando' | 'enviado' | 'falhou';
    em: string | null;
    erro: string | null;
  } | null;
}

export interface Resposta {
  resumo: {
    carteira: number;
    comProduto: number;
    ok: number;
    faltaInstalar: number;
    instaladoSemProduto: number;
    recolher: number;
    migracao: number;
    aguardando: number;
    porClassificacao: { classificacao: string; placas: number }[];
    semInstalar: {
      faixas: { id: Faixa; rotulo: string; placas: number }[];
      maisAntigo: number | null;
      mediana: number | null;
    };
  };
  cobertura: { consultadas: number; total: number; ultimaConsulta: string | null };
  itens: Item[];
}

export const CASOS: Record<Caso, { rotulo: string; aba: string; tom: string }> = {
  falta_instalar: { rotulo: 'falta instalar', aba: 'Falta instalar', tom: 'var(--danger)' },
  instalado_sem_produto: {
    rotulo: 'instalado sem produto',
    aba: 'Sem produto',
    tom: 'var(--warn)',
  },
  recolher: { rotulo: 'recolher', aba: 'Recolher', tom: 'var(--warn)' },
  ok: { rotulo: 'instalado', aba: 'Instalados', tom: 'var(--success)' },
  migracao: { rotulo: 'migração', aba: 'Migração', tom: 'var(--muted)' },
  aguardando: { rotulo: 'aguardando consulta', aba: 'Aguardando', tom: 'var(--faint)' },
};

/** Cor por faixa: quanto mais tempo esperando, mais quente. */
export const TOM_FAIXA: Record<Faixa, string> = {
  ate7: 'var(--success)',
  ate15: 'var(--muted)',
  ate30: 'var(--warn)',
  mais30: 'var(--danger)',
};

export const ROTULO_FAIXA: Record<Faixa, string> = {
  ate7: 'até 7 dias',
  ate15: '8 a 15 dias',
  ate30: '16 a 30 dias',
  mais30: 'mais de 30 dias',
};

export const FAIXAS: Faixa[] = ['ate7', 'ate15', 'ate30', 'mais30'];

export const dataBr = (iso: string | null): string =>
  iso ? new Date(`${iso}T12:00`).toLocaleDateString('pt-BR') : '—';

/** Rua, número e complemento — a linha que se lê para o técnico. */
export const rua = (a: Associado): string | null =>
  [[a.logradouro, a.numero].filter(Boolean).join(', '), a.complemento]
    .filter(Boolean)
    .join(' — ') || null;

/** Bairro · Cidade/UF · CEP. */
export const local = (a: Associado): string | null =>
  [
    a.bairro,
    [a.cidade, a.estado].filter(Boolean).join('/'),
    a.cep ? `CEP ${formatarCep(a.cep)}` : null,
  ]
    .filter(Boolean)
    .join(' · ') || null;

export const enderecoCompleto = (a: Associado): string | null =>
  [rua(a), local(a)].filter(Boolean).join(' · ') || null;

export const contatoPrincipal = (a: Associado): string | null => a.celular ?? a.telefone;

/**
 * Cidade como chave de agrupamento. O SGA grafa a mesma cidade com e sem
 * acento ("RONDONóPOLIS", "RONDONÓPOLIS"), então a chave ignora os dois.
 */
export const chaveCidade = (a: Associado): string =>
  `${(a.cidade ?? 'SEM CIDADE')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toUpperCase()}|${a.estado ?? ''}`;

export const rotuloCidade = (a: Associado): string =>
  a.cidade ? `${a.cidade}${a.estado ? `/${a.estado}` : ''}` : 'Sem cidade';

/** Contexto que o layout da seção passa às abas. */
export interface ContextoRastreamento {
  qs: string;
}

export function useDadosRastreamento() {
  const { qs } = useOutletContext<ContextoRastreamento>();
  return useQuery({
    queryKey: ['rastreamento', qs],
    queryFn: () => api.get<Resposta>(`/rastreamento?${qs}`),
  });
}
